/**
 * Brand Bible v2 draft sync: the diff-syncs and pushes that mirror the page's flat draft into
 * the v2 (bb_) rows. Moved out of BrandBiblePage.tsx (3,000-line rule) as a custom hook with
 * NO behaviour change:
 * - called unconditionally, at the position where the block sat in the page (above the early
 *   return), so the hook order is unchanged; the four in-flight/queued refs moved with it;
 * - the returned functions are deliberately NOT memoised: they are recreated every render and
 *   read the current `identity` / `primaryLocation`, exactly as before (memoising them would
 *   capture stale rows; commit 8ee953427 fixed exactly that class of bug).
 */
import { useRef } from 'react';
import { message } from 'antd';

import { brandBibleV2Service } from '@/services/brandBibleV2Service';
import { CONVERTED_SECTIONS } from '@/components/brand-bible/save';
import { stripConvertedFlatColumns } from '@/lib/bbLegacyFieldOwnership';
import { useBrandBibleWizardStore } from '@/stores/brandBibleWizardStore';
import type { BbEntry, BbPatchPayload, BbPayloadMap, BbTable, IdentityPayload } from '@/features/brand-bible-wizard/types';
import type { BbLetter } from '@/lib/bbSectionMap';
import { buildLegacyOnlyPayload, type BrandBibleDraft } from './draftModel';

/** The store's write actions with the page's tenant already bound in (the store requires the tenant on every write). */
export type BoundUpsert = <T extends BbTable>(
  table: T,
  payload: BbPatchPayload<T>,
  id?: string,
) => Promise<BbEntry<T> | null>;
export type BoundDiscard = (table: BbTable, id: string) => Promise<void>;
export type BoundVerify = (table: BbTable, id: string, approveForPublic?: boolean) => Promise<void>;

/** A web-gathered row nobody has confirmed yet: only the confirm/discard actions may touch it. */
const isDraftRow = (e: { source?: unknown; verified_by?: unknown } | null | undefined): boolean =>
  !!e && e.source === 'discovered' && (e.verified_by === null || e.verified_by === undefined);

export interface V2DraftSyncInput {
  currentTenant: string | null;
  tenantWebsite: string;
  /** The bb_identity singleton (`useSingleton('identity')`); read at call time, per render. */
  identity: { entry: BbEntry<'identity'> | null | undefined };
  primaryLocation: BbEntry<'locations'> | null;
  /** The draft as hydrated from the server; pushDraftToV2 pushes only what differs from it. */
  hydratedDraft: BrandBibleDraft | null;
  saveIdentity: (patch: Partial<IdentityPayload>) => Promise<unknown>;
  identitySaveErrorToast: (field: string) => (e: unknown) => void;
  upsertV2: BoundUpsert;
  discardV2: BoundDiscard;
}

export function useV2DraftSync({
  currentTenant,
  tenantWebsite,
  identity,
  primaryLocation,
  hydratedDraft,
  saveIdentity,
  identitySaveErrorToast,
  upsertV2,
  discardV2,
}: V2DraftSyncInput) {
  // The store is global and flips on init(): a closure made for tenant X is stale once the store
  // is on Y, and must then send nothing and never re-init X (bug: tenant switch wrote to the new client).
  const isStale = () => useBrandBibleWizardStore.getState().tenantId !== currentTenant;

  const savePrimaryLocation = (patch: Partial<BbPayloadMap['locations']>) =>
    upsertV2('locations', { location_key: 'primary', ...patch }, primaryLocation?.id).catch(
      (e: unknown) =>
        message.error(`Failed to save location — ${e instanceof Error ? e.message : 'request failed'}`),
    );

  // §7.5 step 7: service_locations/services_offered are arrays with no single
  // field to PATCH — diff against the current v2 rows and add/remove, the same
  // shape as setCompetitors' blocklist diff below. Non-primary bb_locations rows
  // are keyed by a slug of the city name; bb_services by lower(name), matching
  // the backend's natural-key dedup (registry.py).
  const slugifyLocationKey = (city: string): string =>
    city.trim().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-+|-+$)/g, '') || 'location';

  // Appendix A.7 rules 2+3: a diff-shaped sync may NEVER run against an
  // unloaded store — the cascade's engine was exactly this: a re-init wiped
  // entries synchronously, a diff-sync ran against that empty window, saw
  // zero locations, and mass-created every one that already existed. Every
  // diff-sync here: (a) awaits a genuinely loaded store (bounded — a failed
  // load means ONE toast and the sync is skipped, never a mass-create AND
  // never a silent no-op-forever either), (b) reads FRESH entries directly
  // from the store rather than the render-time v2LocationsEntries/
  // v2ServicesEntries closures (those can be stale by the time an awaited
  // fetch resolves — the exact "stale closure inside a promise
  // continuation" lesson from saveIdentity's single-flight fix), and
  // (c) serializes its writes (sequential, not Promise.allSettled in
  // parallel) — this removes the burst-of-~16-concurrent-creates class
  // entirely, regardless of which client-side stall mechanism was real.
  // R55 B1: a list sync only ever discards a row that (a) was in the hydrated list, so the person
  // saw it, (b) is no longer in the list they have now, and (c) is not discovered-source (a web
  // draft, or one a person confirmed since: both are changed only by confirm / discard actions).
  // Returns true when every write it attempted succeeded.
  const runLocationsDiffSync = async (next: string[]): Promise<boolean> => {
    if (!currentTenant || isStale()) return false;
    const loaded = await useBrandBibleWizardStore.getState().awaitLoaded(currentTenant);
    if (!loaded) {
      if (!isStale()) message.error('Could not load current service locations — try again.');
      return false;
    }
    let ok = true;
    const hydratedKeys = new Set(
      (hydratedDraft?.service_locations ?? []).map((c) => c.trim().toLowerCase()).filter(Boolean),
    );
    const fresh = (useBrandBibleWizardStore.getState().entries.locations ?? []).filter(
      (e) => e.deleted_at === null,
    );
    const existing = fresh.filter((e) => e.location_key !== 'primary');
    // Drafts count as existing (no duplicate create) but are never discarded here.
    const existingByMetro = new Map(existing.map((e) => [(e.metro ?? '').trim().toLowerCase(), e]));
    const nextKeys = new Set(next.map((c) => c.trim().toLowerCase()).filter(Boolean));

    for (const city of next) {
      const key = city.trim().toLowerCase();
      if (!key || existingByMetro.has(key)) continue;
      if (isStale()) return false;
      try {
        await upsertV2('locations', { location_key: slugifyLocationKey(city), metro: city });
      } catch {
        ok = false;
        message.error(`Failed to save service location "${city}"`);
      }
    }
    for (const [key, entry] of existingByMetro) {
      if (nextKeys.has(key) || !hydratedKeys.has(key) || (entry.source === 'discovered' && !entry.verified_by)) continue;
      if (isStale()) return false;
      try {
        await discardV2('locations', entry.id);
      } catch {
        ok = false;
        message.error('Failed to remove a service location');
      }
    }
    return ok;
  };

  // Single-flight per collection: an overlapping sync (autofill's whole-array
  // push racing a manual chip edit) must never run two diffs concurrently —
  // the latest array wins via a queued re-run, same pattern as
  // identityQueuedPatchRef in saveIdentity.
  // Resolves true when this call's own list was fully written; a call that only queued behind a
  // running sync resolves false (the queued run still happens, the page just retries the baseline).
  const locationsSyncInFlightRef = useRef<Promise<boolean> | null>(null);
  const locationsSyncQueuedRef = useRef<string[] | null>(null);
  const syncServiceLocationsV2 = (next: string[]): Promise<boolean> => {
    if (locationsSyncInFlightRef.current) {
      locationsSyncQueuedRef.current = next;
      return locationsSyncInFlightRef.current.then(() => false);
    }
    const run = async (input: string[]): Promise<boolean> => {
      const ok = await runLocationsDiffSync(input);
      locationsSyncInFlightRef.current = null;
      const queued = locationsSyncQueuedRef.current;
      locationsSyncQueuedRef.current = null;
      if (queued) void syncServiceLocationsV2(queued);
      return ok;
    };
    const promise = run(next);
    locationsSyncInFlightRef.current = promise;
    return promise;
  };

  const runServicesDiffSync = async (next: string[]): Promise<boolean> => {
    if (!currentTenant || isStale()) return false;
    const loaded = await useBrandBibleWizardStore.getState().awaitLoaded(currentTenant);
    if (!loaded) {
      if (!isStale()) message.error('Could not load current services offered — try again.');
      return false;
    }
    let ok = true;
    const hydratedKeys = new Set(
      (hydratedDraft?.services_offered ?? []).map((n) => n.trim().toLowerCase()).filter(Boolean),
    );
    const fresh = (useBrandBibleWizardStore.getState().entries.services ?? []).filter(
      (e) => e.deleted_at === null,
    );
    const nextKeys = new Set(next.map((n) => n.trim().toLowerCase()).filter(Boolean));

    for (const name of next) {
      const key = name.trim().toLowerCase();
      if (!key || fresh.some((e) => (e.name ?? '').trim().toLowerCase() === key)) continue;
      if (isStale()) return false;
      try {
        await upsertV2('services', { name });
      } catch {
        ok = false;
        message.error(`Failed to save service "${name}"`);
      }
    }
    for (const entry of fresh) {
      const key = (entry.name ?? '').trim().toLowerCase();
      if (!key || nextKeys.has(key) || !hydratedKeys.has(key) || (entry.source === 'discovered' && !entry.verified_by)) continue;
      if (isStale()) return false;
      try {
        await discardV2('services', entry.id);
      } catch {
        ok = false;
        message.error('Failed to remove a service');
      }
    }
    return ok;
  };

  const servicesSyncInFlightRef = useRef<Promise<boolean> | null>(null);
  const servicesSyncQueuedRef = useRef<string[] | null>(null);
  const syncServicesOfferedV2 = (next: string[]): Promise<boolean> => {
    if (servicesSyncInFlightRef.current) {
      servicesSyncQueuedRef.current = next;
      return servicesSyncInFlightRef.current.then(() => false);
    }
    const run = async (input: string[]): Promise<boolean> => {
      const ok = await runServicesDiffSync(input);
      servicesSyncInFlightRef.current = null;
      const queued = servicesSyncQueuedRef.current;
      servicesSyncQueuedRef.current = null;
      if (queued) void syncServicesOfferedV2(queued);
      return ok;
    };
    const promise = run(next);
    servicesSyncInFlightRef.current = promise;
    return promise;
  };

  // §7.5 sequencing: v2-active tenants close the legacy PUT/POST (§7.3, 409) —
  // this pushes the §A fields that now have v2 homes instead of that body.
  // Field-level onBlur/onChange handlers already do this per-edit; this is the
  // safety net for a whole-draft apply (autofill, "Use this Brand Bible").
  // Resolves to the subset of `d` that is now saved (the keys attempted and written
  // successfully). A failed write, and a field skipped because its row is an unconfirmed draft,
  // are NOT in it, so the page advances its baseline per key and retries the rest next time.
  const pushDraftToV2 = async (d: BrandBibleDraft): Promise<Partial<BrandBibleDraft>> => {
    const saved: Partial<BrandBibleDraft> = {};
    if (isStale()) return saved;
    const h = hydratedDraft;
    // Before hydration there is no baseline to diff against: push nothing.
    if (h === null) return saved;
    const writes: Promise<unknown>[] = [];
    const sameList = (a: string[], b: string[]) => a.length === b.length && a.every((v, i) => v === b[i]);
    type IdentityKey = 'business_name' | 'website' | 'phone' | 'email' | 'description';
    const identityWrites: Array<[IdentityKey, keyof IdentityPayload, string]> = [
      ['business_name', 'legal_name', 'business name'],
      ['website', 'website', 'website'],
      ['phone', 'phone', 'phone'],
      ['email', 'email', 'email'],
      ['description', 'brand_overview', 'brand overview'],
    ];
    if (!isDraftRow(identity.entry)) {
      for (const [key, v2Key, label] of identityWrites) {
        if (d[key] === h[key]) continue;
        writes.push(
          saveIdentity({ [v2Key]: d[key] }).then(
            () => {
              saved[key] = d[key];
            },
            identitySaveErrorToast(label),
          ),
        );
      }
    }
    if (!isDraftRow(primaryLocation) && d.address !== h.address) {
      writes.push(
        upsertV2('locations', { location_key: 'primary', address: d.address }, primaryLocation?.id).then(
          () => {
            saved.address = d.address;
          },
          (e: unknown) =>
            message.error(`Failed to save location — ${e instanceof Error ? e.message : 'request failed'}`),
        ),
      );
    }
    // A list sync runs only for a list that changed since hydration (R55 B1).
    if (!sameList(d.service_locations, h.service_locations)) {
      writes.push(
        syncServiceLocationsV2(d.service_locations).then((ok) => {
          if (ok) saved.service_locations = d.service_locations;
        }),
      );
    }
    if (!sameList(d.services_offered, h.services_offered)) {
      writes.push(
        syncServicesOfferedV2(d.services_offered).then((ok) => {
          if (ok) saved.services_offered = d.services_offered;
        }),
      );
    }
    await Promise.allSettled(writes);
    return saved;
  };

  // §7.6: the ~11 flat columns that are genuinely legacy_only (see
  // buildLegacyOnlyPayload's comment for the 5 that are NOT — those stay
  // unrouted). One PATCH per push carrying the whole current draft snapshot
  // for these columns — matches the pre-guard legacy autosave's own
  // whole-row-per-save semantics, just scoped to fewer columns and a
  // different endpoint; the server never nulls a column not in the payload,
  // and every field here is always present, so nothing regresses.
  const pushLegacyOnlyFieldsToV2 = async (
    d: BrandBibleDraft,
    includeLetters?: readonly BbLetter[],
  ): Promise<void> => {
    if (!currentTenant || isStale()) return;
    try {
      // Explicit-save contract: columns owned by a CONVERTED section are the
      // section's Save button's to persist — autosave must not race it. The
      // committing letter passes itself via includeLetters to get back in.
      const payload = stripConvertedFlatColumns(
        buildLegacyOnlyPayload(d, tenantWebsite || currentTenant),
        CONVERTED_SECTIONS,
        includeLetters,
      );
      if (Object.keys(payload).length === 0) return;
      await brandBibleV2Service.patchLegacyFields(currentTenant, payload);
    } catch (e: unknown) {
      message.error(
        `Failed to save some fields — ${e instanceof Error ? e.message : 'request failed'}`,
      );
    }
  };

  return {
    savePrimaryLocation,
    syncServiceLocationsV2,
    syncServicesOfferedV2,
    pushDraftToV2,
    pushLegacyOnlyFieldsToV2,
  };
}
